import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { login, sendOtp, loginWithOtp } from '../store/authActions';
import { Box, Typography, CircularProgress, Button, alpha } from '@mui/material';
import InputComponent from 'src/components/shared/Form/Input/index.js';
import { palette, shadows } from 'src/config/theme.js';
import { styled } from '@mui/material/styles';
import { Logo } from 'src/layout/styles';
import { Component1, Component4 } from './style';
import { enqueueSnackbar } from 'notistack';

export const Texts = styled(Typography)({
  color: palette.gery.black,
});

function Login() {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);

  const [isOtpLogin, setIsOtpLogin] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const initialValues = {
    email: '',
    password: '',
    otp: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email address').required('Please enter your email!'),
    password: isOtpLogin ? Yup.string() : Yup.string().min(8, 'Password must be at least 8 characters long!').required('Please enter your password!'),
    otp: otpSent ? Yup.string().matches(/^\d+$/, 'OTP must contain only numbers').required('Please enter your OTP!') : Yup.string(),
  });

  const handleSubmit = async (values, form) => {
    console.log('Form Submitted with Values:', values);
    
    return new Promise((resolve, reject) => {
      if (isOtpLogin) {
        if (!otpSent) {
          console.log('Sending OTP for Email:', values.email);
          dispatch(
            sendOtp(values.email, () => {
              console.log('OTP Sent Successfully');
              enqueueSnackbar('OTP sent to your email!', { variant: 'success' });
              setOtpSent(true);
              resolve();
            }, (error) => {
              console.error('Failed to send OTP:', error);
              enqueueSnackbar(error?.error || 'Failed to send OTP!', { variant: 'error' });
              reject(error);
            })
          );
        } else {
          console.log('Logging in with OTP:', values.otp);
          dispatch(
            loginWithOtp(values, () => {
              console.log('OTP Login Successful');
              enqueueSnackbar('Login successful!', { variant: 'success' });
              resolve();
              setIsOtpLogin(false);
              setOtpSent(false);
            }, (error) => {
              console.error('OTP Login Failed:', error);
              enqueueSnackbar(error?.error || 'OTP login failed!', { variant: 'error' });
              reject(error);
            })
          );
        }
      } else {
        console.log('Logging in with Password for Email:', values.email);
        dispatch(
          login(values, resolve, (error) => {
            console.error('Login Failed:', error);
            enqueueSnackbar(error?.message || 'Login failed!', { variant: 'error' });
            reject(error);
          })
        );
      }
    });
  };

  return (
    <Box
      sx={{
        mx: 'auto',
        height: '100vh',
        overflow: 'hidden',
        p: 2,
        bgcolor: '#F8F9FB',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Logo sx={{ paddingBottom: '15px',width:'200px',height:'45px' }} />
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, handleChange, handleSubmit, touched, errors, isValid }) => (
          <form onSubmit={handleSubmit} noValidate>
            <Box
              sx={{
                width: 350,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '20px 16px 24px 16px',
                gap: '18px',
              }}
            >
              <Component1>
                <Texts>Email</Texts>
                <InputComponent
                  type="email"
                  variant="outlined"
                  placeholder="Enter your email"
                  fullWidth
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  sx={{
                    backgroundColor: palette.gery.white,
                    boxShadow: shadows[1],
                    '& .MuiOutlinedInput-root': {
                      padding: '10px 0px',
                      borderRadius: '12px',
                      border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                      height: '44px',
                    },
                  }}
                />
              </Component1>

              {!isOtpLogin && (
                <Component1>
                  <Texts>Password</Texts>
                  <InputComponent
                    type="password"
                    variant="outlined"
                    placeholder="Enter your password"
                    fullWidth
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    sx={{
                      backgroundColor: palette.gery.white,
                      boxShadow: shadows[1],
                      '& .MuiOutlinedInput-root': {
                        padding: '10px 0px',
                        borderRadius: '12px',
                        border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                        height: '44px',
                      },
                    }}
                  />
                </Component1>
              )}

              {isOtpLogin && otpSent && (
                <Component1>
                  <Texts>Enter OTP</Texts>
                  <InputComponent
                    type="text"
                    variant="outlined"
                    placeholder="Enter OTP"
                    fullWidth
                    name="otp"
                    value={values.otp}
                    onChange={handleChange}
                    error={touched.otp && Boolean(errors.otp)}
                    helperText={touched.otp && errors.otp}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} 
                    sx={{
                      backgroundColor: palette.gery.white,
                      boxShadow: shadows[1],
                      '& .MuiOutlinedInput-root': {
                        padding: '10px 0px',
                        borderRadius: '12px',
                        border: `1px solid ${alpha(palette.gery.black, 0.14)}`,
                        height: '44px',
                      },
                    }}
                  />
                </Component1>
              )}

              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
                <Texts
                  variant='h6'
                  sx={{
                    textAlign: 'right',
                    marginTop: '10px',
                    textDecoration: 'none',
                    color: palette.primary.main,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => { setIsOtpLogin(!isOtpLogin); setOtpSent(false); }}
                >
                  {isOtpLogin ? 'Login with Password' : 'Login with OTP'}
                </Texts>
              </Box>
            </Box>
          
            <Component4>
              <Button
                variant="contained"
                fullWidth
                type="submit"
                sx={{ height: '44px', borderRadius: '12px' }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : otpSent ? 'Login' : isOtpLogin ? 'Send OTP' : 'Login'}
              </Button>
            </Component4>
          </form>
        )}
      </Formik>
    </Box>
  );
}

export default Login;
